import { TRANSFER_DETAILS_VIEW } from '@stocks/StocksInventories/Transfers/utils/constants';

const getUserTransferListAssociations = (transferLists, stores) => {
  const associatedStoreIds = new Set(stores.map(({ id }) => id));

  return transferLists.reduce(
    (acc, transferList) => {
      const isAssociatedToSender = associatedStoreIds.has(transferList.senderStoreId);
      const isAssociatedToRecipient = associatedStoreIds.has(transferList.recipientStoreId);

      if (isAssociatedToSender && isAssociatedToRecipient) {
        acc.both.push(transferList);
        return acc;
      }

      if (isAssociatedToSender) {
        acc.sender.push(transferList);
        return acc;
      }

      if (isAssociatedToRecipient) {
        acc.recipient.push(transferList);
        return acc;
      }

      acc.none.push(transferList);

      return acc;
    },
    {
      sender: [],
      recipient: [],
      both: [],
      none: [],
    },
  );
};

const hasDifferentQuantities = (
  { transferNoteQuantity, deliveryNoteQuantity, receivedQuantity },
  transferDetailsView,
) => {
  if (transferDetailsView === TRANSFER_DETAILS_VIEW.DELIVERY) {
    // If no delivery quantity has been set, there is nothing to compare to
    // Therefore, the quantity is the same
    if (deliveryNoteQuantity === null) {
      return false;
    }

    return deliveryNoteQuantity !== transferNoteQuantity;
  }

  if (transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION) {
    // Same logic for reception
    if (receivedQuantity === null) {
      return false;
    }

    return receivedQuantity !== deliveryNoteQuantity;
  }

  return false;
};

const getTransferQuantityToUpdate = (transferDetailsView) => {
  switch (transferDetailsView) {
    case TRANSFER_DETAILS_VIEW.DRAFT:
      return 'transferNoteQuantity';
    case TRANSFER_DETAILS_VIEW.DELIVERY:
      return 'deliveryNoteQuantity';
    case TRANSFER_DETAILS_VIEW.RECEPTION:
      return 'receivedQuantity';
    default:
      return 'transferNoteQuantity';
  }
};

/**
 * Returns the quantity to display in the datagrid based on the transfer details view.
 *
 * Rules:
 * - Prioritize quantities that have been explicitly set.
 * - `null` → quantity has not been specified yet for the current status
 *   (e.g.: reception quantities when creating a draft).
 * - `0` → transfer has been saved but quantity is explicitly zero
 *   (e.g.: transfer note quantity for packaging with no assigned quantities).
 *
 * @param {{transferNoteQuantity: number, deliveryNoteQuantity: number, receivedQuantity: number}} transfer
 *        Quantities for the transfer in its different states.
 * @param {string} transferDetailsView
 *        Current view of the transfer details.
 * @returns {number|null} Quantity to display, or `null` if not set.
 */
const getTransferQuantityToDisplay = (
  { transferNoteQuantity, deliveryNoteQuantity, receivedQuantity },
  transferDetailsView,
) => {
  if (transferDetailsView === TRANSFER_DETAILS_VIEW.DRAFT) {
    return transferNoteQuantity ?? null;
  }

  if (
    [TRANSFER_DETAILS_VIEW.DELIVERY, TRANSFER_DETAILS_VIEW.SENDER_DISABLED].includes(
      transferDetailsView,
    )
  ) {
    return deliveryNoteQuantity ?? transferNoteQuantity ?? null;
  }

  if (
    [TRANSFER_DETAILS_VIEW.RECEPTION, TRANSFER_DETAILS_VIEW.DISABLED].includes(transferDetailsView)
  ) {
    return receivedQuantity ?? deliveryNoteQuantity ?? transferNoteQuantity ?? null;
  }

  return null;
};

export default {
  getUserTransferListAssociations,
  getTransferQuantityToUpdate,
  getTransferQuantityToDisplay,
  hasDifferentQuantities,
};
